import { useHead } from '@unhead/react';
import { useTranslation } from 'react-i18next';

const SITE_ORIGIN = 'https://knitintime.com';
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-default.png`;

export interface PageMetaProps {
  title: string;
  description: string;
  /** Path component starting with `/`. Always include trailing slash for indexed routes (e.g. `/learn/features/`). */
  path: string;
  /** Set true on app-only routes that should not be indexed. */
  noindex?: boolean;
  /** Optional JSON-LD `@type` payload. `@context` is added automatically. */
  structuredData?: Record<string, unknown>;
  /** Optional override for the OG/Twitter image. Defaults to `og-default.png`. */
  ogImage?: string;
}

/**
 * Per-page head/meta. Delegates to `@unhead/react`'s useHead, which:
 *   - Collects tags during SSR (renderSSRHead in scripts/prerender.tsx)
 *   - Imperatively manages document.head on the client, deduplicating by
 *     tag identity (name= / property= / rel=) so SPA navigation replaces
 *     prior tags rather than stacking new ones
 * Bypasses React 19's metadata hoisting entirely — that hoisting doesn't
 * deduplicate against tags already in <head>, which is exactly the trap
 * the prerender + hydration combo fell into.
 */
export function PageMeta({
  title,
  description,
  path,
  noindex,
  structuredData,
  ogImage = DEFAULT_OG_IMAGE,
}: PageMetaProps) {
  const { t } = useTranslation('seo');
  const siteName = t('siteName', 'Knit in Time');
  const canonical = `${SITE_ORIGIN}${path}`;
  const jsonLd = structuredData
    ? JSON.stringify({ '@context': 'https://schema.org', ...structuredData })
    : null;

  useHead({
    title,
    meta: [
      { name: 'description', content: description },
      ...(noindex ? [{ name: 'robots', content: 'noindex' }] : []),
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: canonical },
      { property: 'og:image', content: ogImage },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: siteName },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage },
    ],
    link: [{ rel: 'canonical', href: canonical }],
    script: jsonLd ? [{ type: 'application/ld+json', innerHTML: jsonLd }] : [],
  });

  return null;
}
